/** @format */

import React from 'react';
import {
  createElement,
  Fragment,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { render } from 'react-dom';
import { usePagination, useSearchBox } from 'react-instantsearch-core';
import { autocomplete, AutocompleteOptions } from '@algolia/autocomplete-js';
import { BaseItem } from '@algolia/autocomplete-core';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';
import { QUERY_SUGGESTIONS } from '@components/search/algolia';

/**
 * Autocomplete Props.
 */
type AutocompleteProps = Partial<AutocompleteOptions<BaseItem>> & {
  className?: string;
  searchClient: SearchClient;
};

/**
 * Set Search UI Type.
 */
type SetInstantSearchUiStateOptions = {
  query: string;
};

/**
 * Simple Autocomplete Search.
 */
const SimpleAutocompleteSearch = ({
  searchClient,
  className,
  ...autocompleteProps
}: AutocompleteProps) => {
  const autocompleteContainer = useRef<HTMLDivElement>(null);
  const { query, refine: setQuery } = useSearchBox();
  const { refine: setPage } = usePagination();
  const [instantSearchUiState, setInstantSearchUiState] =
    useState<SetInstantSearchUiStateOptions>({ query });

  /**
   * Plugins.
   */
  const plugins = useMemo(() => {
    /**
     * Recent Searches
     */
    const recentSearches = createLocalStorageRecentSearchesPlugin({
      key: 'instantsearch',
      limit: 3,
      transformSource({ source }) {
        return {
          ...source,
          onSelect({ item }) {
            setInstantSearchUiState({ query: item.label });
          },
        };
      },
    });

    /**
     * Query Suggestions
     */
    const querySuggestions = createQuerySuggestionsPlugin({
      searchClient,
      indexName: QUERY_SUGGESTIONS,
      getSearchParams() {
        // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
        return recentSearches?.data!.getAlgoliaSearchParams({
          hitsPerPage: 6,
        });
      },
      transformSource({ source }) {
        return {
          ...source,
          sourceId: 'querySuggestionsPlugin',
          onSelect({ item }) {
            setInstantSearchUiState({ query: item.query });
          },
          getItems(params) {
            if (!params.state.query) {
              return [];
            }

            return source.getItems(params);
          },
        };
      },
    });

    return [recentSearches, querySuggestions];
  }, [searchClient]);

  /**
   * Set initial UI state.
   */
  useEffect(() => {
    setQuery(instantSearchUiState.query);
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instantSearchUiState]);

  /**
   * Autocomplete Search.
   */
  useEffect(() => {
    if (!autocompleteContainer.current) {
      return;
    }

    const autocompleteInstance = autocomplete({
      ...autocompleteProps,
      container: autocompleteContainer.current,
      initialState: { query },
      onReset() {
        setInstantSearchUiState({ query: '' });
      },
      onSubmit({ state }) {
        setInstantSearchUiState({ query: state.query });
      },
      onStateChange({ prevState, state }) {
        if (prevState.query !== state.query) {
          setInstantSearchUiState({
            query: state.query,
          });
        }
      },
      renderer: { createElement, Fragment, render: render as Render },
      plugins,
    });

    return () => autocompleteInstance.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plugins]);

  /**
   * Render.
   */
  return <div className={className} ref={autocompleteContainer} />;
};

export default SimpleAutocompleteSearch;
